/**
 * Just-in-time hints — the engine (AD-844; docs/design/guest-demo.md §5.7). Pure: every function
 * takes `now` and returns new state, so the rules are testable without timers or React.
 *
 * Rules (the design's, in code):
 * 1. **One at a time.** A hint is shown when its trigger holds, it is not yet done, and it was never
 *    shown on this browser; the catalogue's order is the priority.
 * 2. **Show once.** Showing a hint records it as seen (persisted by the caller); it is never shown
 *    again, whatever happens next.
 * 3. **It leaves on its own.** It retires when the player does what it asked (`acted`), when they
 *    dismiss it, or after {@link HINT_MAX_MS}; the next one waits {@link HINT_GAP_MS} so two never
 *    flash past each other.
 */

import type { HintId, QuestWire } from '@great-cloud/shared/client';

/** How the player is playing: the words follow the input (touch wording on touch, keys on desktop). */
export type HintInput = 'touch' | 'desktop';

/** What the triggers read — a snapshot the HUD builds from the store each render. */
export interface HintContext {
  input: HintInput;
  quests: readonly QuestWire[];
  /** The avatar has walked away from where it arrived. */
  moved: boolean;
  /** A live hostile is within aggro reach. */
  hostileNear: boolean;
  /** The demo's boss is near (its name matches the catalogue's boss match). */
  bossNear: boolean;
  /** Own health / max, 0..1; 1 when unknown. */
  hpFrac: number;
  dead: boolean;
  /** At least one of the player's own blows or abilities has landed this session. */
  struck: boolean;
  /** The 1-based bar slot (= key) holding an ability id, or null when it is not on the bar. */
  slotOf: (abilityId: string) => number | null;
}

export interface HintDef {
  id: HintId;
  /** The trigger: show (once) when this holds. */
  when: (ctx: HintContext) => boolean;
  /** The player did what it asked: retire it as acted on. */
  done: (ctx: HintContext) => boolean;
  /** The words, for this input. Short; never a tuning number (AD-321). */
  text: (ctx: HintContext) => string;
}

/** A shown hint retires after this long even if nothing happened (ms). */
export const HINT_MAX_MS = 20_000;
/** The pause between one hint leaving and the next appearing (ms). */
export const HINT_GAP_MS = 1_500;

export interface HintState {
  active: { id: HintId; shownAt: number } | null;
  /** Every hint ever shown on this browser (persisted by the caller). */
  seen: ReadonlySet<string>;
  /** When the last hint left (ms), for the gap. */
  retiredAt: number;
}

export interface HintEvent {
  hintId: HintId;
  event: 'shown' | 'acted';
}

export function initialHintState(seen: Iterable<string> = []): HintState {
  return { active: null, seen: new Set(seen), retiredAt: Number.NEGATIVE_INFINITY };
}

/**
 * Advance the hints one step: retire the active one if it was acted on or ran its time, else pick
 * the next. Returns the new state and the events to count (`shown` / `acted`).
 */
export function stepHints(
  defs: readonly HintDef[],
  state: HintState,
  ctx: HintContext,
  now: number,
): { state: HintState; events: HintEvent[] } {
  const events: HintEvent[] = [];
  let next = state;
  if (next.active) {
    const def = defs.find((d) => d.id === next.active!.id);
    if (!def || def.done(ctx)) {
      if (def) events.push({ hintId: def.id, event: 'acted' });
      next = { ...next, active: null, retiredAt: now };
    } else if (now - next.active.shownAt >= HINT_MAX_MS) {
      next = { ...next, active: null, retiredAt: now };
    }
  }
  if (!next.active && now - next.retiredAt >= HINT_GAP_MS) {
    const pick = defs.find((d) => !next.seen.has(d.id) && d.when(ctx) && !d.done(ctx));
    if (pick) {
      const seen = new Set(next.seen);
      seen.add(pick.id);
      next = { ...next, active: { id: pick.id, shownAt: now }, seen };
      events.push({ hintId: pick.id, event: 'shown' });
    }
  }
  return { state: next, events };
}

/** The player closed the active hint: it leaves now and stays seen. */
export function dismissHint(state: HintState, now: number): HintState {
  return state.active ? { ...state, active: null, retiredAt: now } : state;
}

/** The storage key of the seen set (localStorage; one list per browser). */
export const HINTS_SEEN_KEY = 'gc.hints.seen.v1';

/** Read the seen set; a missing or unreadable entry is an empty set (fail open: hints show). */
export function loadSeen(storage: Pick<Storage, 'getItem'> | undefined): string[] {
  try {
    const raw = storage?.getItem(HINTS_SEEN_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function saveSeen(
  storage: Pick<Storage, 'setItem'> | undefined,
  seen: ReadonlySet<string>,
): void {
  try {
    storage?.setItem(HINTS_SEEN_KEY, JSON.stringify([...seen]));
  } catch {
    // private mode / quota: the hints still show once per page load
  }
}

// ---- quest helpers the catalogue's triggers use ----

export function questOf(ctx: HintContext, questId: string): QuestWire | undefined {
  return ctx.quests.find((q) => q.questId === questId);
}

/** True while `questId` is accepted and its objective with `verb` is not yet done. */
export function objectiveOpen(ctx: HintContext, questId: string, verb: string): boolean {
  const q = questOf(ctx, questId);
  return q?.status === 'active' && q.objectives.some((o) => o.verb === verb && !o.done);
}

/** True once `questId`'s objective with `verb` has any progress (or the quest moved on). */
export function objectiveStarted(ctx: HintContext, questId: string, verb: string): boolean {
  const q = questOf(ctx, questId);
  if (!q) return false;
  if (q.status !== 'active') return true;
  return q.objectives.some((o) => o.verb === verb && (o.done || o.progress > 0));
}
